import { extractAgentArtifact } from "@/components/ai-chat/agent-execution-adapter";
import { ChatResponseStatus } from "@/components/ai-chat/types";

import { queryClient } from "@/config/react-query";

import {
  cancelledResponseKey,
  projectCancelledAskDecodaEntry,
} from "@/hooks/ask-decoda/ask-decoda-cancelled-presentation";
import {
  askDecodaHistoryQueryOptions,
  invalidateAskDecodaHistoryReads,
} from "@/hooks/ask-decoda/ask-decoda-history-query";

import {
  patchHistoryEntry,
  replaceCommittedHistoryEntry,
  upsertThreadSummary,
} from "@/lib/ask-decoda-history-cache";

import { AgentExecutionStatus } from "@/types/agent-execution";
import { AskDecodaCustomEventType } from "@/types/chat-thread";
import { AskDecodaRetryStatus } from "@/types/chat-thread";
import type {
  AskDecodaStreamEvent,
  ChatHistoryEntry,
  ChatThreadSummary,
} from "@/types/chat-thread";

import {
  askDecodaThreadHistoryQueryKey,
  askDecodaThreadsQueryKey,
  getAskDecodaThreads,
  setNativeAskDecodaReadState,
} from "@/api/ai/chat-threads";
import { kyInstance } from "@/api/api-config";
import { invalidateAgentArtifact } from "@/api/utils/agent-cache-invalidation";
import { useAskDecodaStore } from "@/stores/ask-decoda-store";

export function getCachedHistory(
  tenant: string | undefined,
  threadId: string,
): ChatHistoryEntry[] {
  return (
    queryClient.getQueryData<ChatHistoryEntry[]>(
      askDecodaThreadHistoryQueryKey(tenant, threadId),
    ) ?? []
  );
}

export function summarizeCachedThread(params: {
  tenantSubDomain: string | undefined;
  threadId: string;
  createdDate: string;
  latestStatus?: ChatThreadSummary["latestStatus"];
  readAt?: string | null;
}): ChatThreadSummary {
  const history = getCachedHistory(
    params.tenantSubDomain,
    params.threadId,
  ).toSorted(
    (a, b) =>
      new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
  );
  const previous = queryClient
    .getQueryData<
      ChatThreadSummary[]
    >(askDecodaThreadsQueryKey(params.tenantSubDomain))
    ?.find((thread) => thread.threadId === params.threadId);
  // Retain server activity for resumes whose display entry keeps its first date,
  // while allowing a newly admitted local operation to advance the summary.
  const latestActivity = new Date(
    Math.max(
      new Date(params.createdDate).getTime(),
      new Date(history.at(-1)?.createdDate ?? params.createdDate).getTime(),
      new Date(previous?.latestActivity ?? params.createdDate).getTime(),
    ),
  ).toISOString();
  return {
    threadId: params.threadId,
    firstQuestion: history[0]?.question || "New conversation",
    messageCount: history.length,
    createdDate: history[0]?.createdDate ?? params.createdDate,
    latestActivity,
    latestStatus: params.latestStatus ?? history.at(-1)?.status,
    readAt: params.readAt,
  };
}

/** Apply server-owned domain data without rebuilding its status, sources or artifacts. */
export function applyAskDecodaCustomEvent(params: {
  tenantSubDomain: string | undefined;
  threadId: string;
  chatId: string;
  event: AskDecodaStreamEvent;
}) {
  const { tenantSubDomain, threadId, chatId, event } = params;
  if (
    event.type === AskDecodaCustomEventType.METADATA ||
    event.type === AskDecodaCustomEventType.HISTORY_SNAPSHOT
  ) {
    const snapshot =
      event.type === AskDecodaCustomEventType.METADATA
        ? event.chatHistory
        : event.entry;
    if (snapshot) {
      const previous = getCachedHistory(tenantSubDomain, threadId).find(
        (entry) => entry.id === snapshot.id,
      );
      for (const execution of snapshot.executions ?? []) {
        const before = previous?.executions?.find(
          (item) => item.id === execution.id,
        );
        const artifact = extractAgentArtifact(execution);
        const previousArtifact = before ? extractAgentArtifact(before) : null;
        if (
          artifact &&
          (before?.status !== AgentExecutionStatus.COMPLETED ||
            before.attempt !== execution.attempt ||
            before.completedAt !== execution.completedAt ||
            previousArtifact?.id !== artifact.id ||
            previousArtifact?.type !== artifact.type)
        ) {
          invalidateAgentArtifact(artifact.type);
        }
      }
      invalidateAskDecodaHistoryReads({
        queryClient,
        tenantSubDomain,
        threadId,
      });
      queryClient.setQueryData<ChatHistoryEntry[]>(
        askDecodaThreadHistoryQueryKey(tenantSubDomain, threadId),
        (current = []) =>
          replaceCommittedHistoryEntry(current, {
            ...projectCancelledAskDecodaEntry(tenantSubDomain, snapshot),
            // The outer identity belongs to the requested subscription. A
            // root alias can project a newer operation on the same response.
            operationChatId: snapshot.operationChatId ?? event.operationChatId,
            requestId: event.requestId,
            requestAccepted: true,
          }),
      );
    }
    return;
  }
  if (
    event.type === AskDecodaCustomEventType.TOOL_PROGRESS ||
    event.type === AskDecodaCustomEventType.MODEL_RETRY
  ) {
    patchHistoryEntry(
      queryClient,
      tenantSubDomain,
      threadId,
      { id: chatId },
      (entry) => ({
        ...entry,
        progressMessage:
          event.type === AskDecodaCustomEventType.TOOL_PROGRESS
            ? event.message
            : event.status === AskDecodaRetryStatus.RETRYING
              ? event.message || "The AI service is temporarily busy. Retrying…"
              : undefined,
      }),
    );
    return;
  }
}

export async function reconcileAskDecodaRun(params: {
  tenantSubDomain: string | undefined;
  threadId: string;
  createdDate: string;
  runtimeEpoch: number;
  /** Includes run/subscription freshness; a newer snapshot invalidates this fetch. */
  isCurrent?: () => boolean;
}) {
  const { tenantSubDomain, threadId, runtimeEpoch } = params;
  const isCurrentTenant = () =>
    useAskDecodaStore.getState().runtimeEpoch === runtimeEpoch &&
    (params.isCurrent?.() ?? true);
  if (!isCurrentTenant()) return;
  const finalStatus = getCachedHistory(tenantSubDomain, threadId).at(
    -1,
  )?.status;
  let readAt: string | null | undefined;
  if (
    finalStatus === ChatResponseStatus.COMPLETED &&
    useAskDecodaStore.getState().activeThreadId === threadId
  ) {
    try {
      readAt = (await setNativeAskDecodaReadState(threadId, true))?.readAt;
    } catch {
      // A failed read receipt must not prevent history reconciliation.
    }
  }
  if (!isCurrentTenant()) return;
  const activeRun = useAskDecodaStore.getState().liveRunsByThreadId[threadId];
  if (activeRun && !params.isCurrent) return;
  // Retire an older observer fetch before fetchQuery can deduplicate onto it.
  invalidateAskDecodaHistoryReads({ queryClient, tenantSubDomain, threadId });
  const [, threads] = await Promise.all([
    queryClient.fetchQuery({
      ...askDecodaHistoryQueryOptions({
        queryClient,
        tenantSubDomain,
        threadId,
        owner: { runId: activeRun?.runId, isCurrent: isCurrentTenant },
      }),
      staleTime: 0,
    }),
    getAskDecodaThreads(),
  ]);
  if (!isCurrentTenant()) return;
  queryClient.setQueryData(askDecodaThreadsQueryKey(tenantSubDomain), threads);
  upsertThreadSummary(
    queryClient,
    tenantSubDomain,
    summarizeCachedThread({ ...params, readAt }),
  );
}

export interface AskDecodaCancellationOwner {
  tenantSubDomain: string | undefined;
  threadId: string;
  responseChatId: string;
  operationChatId: string;
  entryOperationId: string;
  runId: string | undefined;
  runtimeEpoch: number;
}

function cancellationEntry(owner: AskDecodaCancellationOwner) {
  const state = useAskDecodaStore.getState();
  const run = state.liveRunsByThreadId[owner.threadId];
  const entry = getCachedHistory(owner.tenantSubDomain, owner.threadId).find(
    (item) => item.id === owner.responseChatId,
  );
  const entryOperationId = entry?.operationChatId ?? entry?.id;
  // A resume's first metadata can replace the displayed predecessor identity
  // while the same captured subscription is still running the stopped operation.
  const operationMatches =
    entryOperationId === owner.entryOperationId ||
    (owner.runId !== undefined &&
      run?.chatId === owner.operationChatId &&
      entryOperationId === owner.operationChatId);
  if (
    state.runtimeEpoch !== owner.runtimeEpoch ||
    run?.runId !== owner.runId ||
    !entry ||
    !operationMatches
  )
    return;
  return entry;
}

/** End browser presentation, never assert durable execution settlement. */
export function cancelAskDecodaPresentation(owner: AskDecodaCancellationOwner) {
  const entry = cancellationEntry(owner);
  if (
    !entry ||
    (entry.status !== ChatResponseStatus.STREAMING &&
      entry.status !== ChatResponseStatus.BLOCKED &&
      entry.status !== ChatResponseStatus.CANCELLED)
  )
    return;
  const state = useAskDecodaStore.getState();
  const { tenantSubDomain, threadId, responseChatId } = owner;
  invalidateAskDecodaHistoryReads({ queryClient, tenantSubDomain, threadId });
  state.markResponseCancelled(
    cancelledResponseKey(tenantSubDomain, responseChatId),
  );
  // Dispose before releasing admission so a replacement cannot be aborted.
  if (owner.runId) state.cancelThreadRun(threadId);
  patchHistoryEntry(
    queryClient,
    tenantSubDomain,
    threadId,
    { id: responseChatId },
    (current) => ({
      ...current,
      status: ChatResponseStatus.CANCELLED,
      progressMessage: undefined,
    }),
  );
  if (owner.runId) state.finishLiveRun(threadId, owner.runId);
  state.finishStopping(threadId, owner.operationChatId);
  upsertThreadSummary(
    queryClient,
    tenantSubDomain,
    summarizeCachedThread({
      tenantSubDomain,
      threadId,
      createdDate: entry.createdDate,
    }),
  );
}

export async function cancelAskDecodaThread(params: {
  threadId: string;
  tenantSubDomain: string | undefined;
}) {
  const { tenantSubDomain, threadId } = params;
  const state = useAskDecodaStore.getState();
  const history = getCachedHistory(tenantSubDomain, threadId);
  const run = state.liveRunsByThreadId[threadId];
  const responseChatId =
    run?.responseChatId ?? run?.chatId ?? history.at(-1)?.id;
  const entry = history.find((item) => item.id === responseChatId);
  if (!entry || !responseChatId) return;
  const operationChatId = run?.chatId ?? entry.operationChatId ?? entry.id;
  if (!state.beginStopping(threadId, operationChatId)) return;
  const owner: AskDecodaCancellationOwner = {
    tenantSubDomain,
    threadId,
    responseChatId,
    operationChatId,
    entryOperationId: entry.operationChatId ?? entry.id,
    runId: run?.runId,
    runtimeEpoch: state.runtimeEpoch,
  };
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    // Covers network transit, dependencies and JSON parsing, not merely the
    // endpoint's internal acknowledgement wait. Keep Stop delivery in flight.
    const deadline = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, 5000);
    });
    const request = (async () => {
      try {
        const response = await kyInstance.post(
          `agent/chat/${operationChatId}/cancel`,
          {
            retry: 0,
            timeout: 20_000,
          },
        );
        const result = await response.json<{ cancelled: boolean }>();
        if (result.cancelled !== true)
          throw new Error("The cancellation request was not accepted.");
      } catch (error) {
        const current = cancellationEntry(owner);
        if (current?.status === ChatResponseStatus.CANCELLED) {
          cancelAskDecodaPresentation(owner);
          return;
        }
        // SSE settlement, a replacement or tenant change already won. Never
        // report an old request's failure against that newer presentation.
        if (
          !current ||
          (current.status !== ChatResponseStatus.STREAMING &&
            current.status !== ChatResponseStatus.BLOCKED)
        )
          return;
        throw error;
      }
    })();
    await Promise.race([request, deadline]);
    cancelAskDecodaPresentation(owner);
  } finally {
    clearTimeout(timer);
    const current = useAskDecodaStore.getState();
    if (current.runtimeEpoch === owner.runtimeEpoch)
      current.finishStopping(threadId, operationChatId);
  }
}
